import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AuthContext from '../../context/AuthContext';
import AppLayout from '../layout/AppLayout';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

// Get API URL with fallback to environment variable
const API_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

const DANGER = '#C2410C';
const WARNING_BG = '#FFF7ED';
const WARNING_RULE = '#F5C99B';
const CONFIRM_WORD = 'RESTORE';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

// The API reports sizes as "12.34 MB"; plain byte counts are handled too.
const formatSize = (size) => {
    if (size == null || size === '') return '–';
    let bytes;
    if (typeof size === 'number') {
        bytes = size;
    } else {
        const match = String(size).trim().match(/^([\d.]+)\s*(B|KB|MB|GB)?$/i);
        if (!match) return String(size);
        const factor = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 }[(match[2] || 'B').toUpperCase()];
        bytes = parseFloat(match[1]) * factor;
    }
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    if (bytes < 1024 ** 2) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
};

// The API reports timestamps as "YYYY-MM-DD HH:mm:ss" in server local time
const parseTimestamp = (value) => {
    if (!value) return null;
    const date = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
    const date = parseTimestamp(value);
    if (!date) return value || '–';
    return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatRelative = (value) => {
    const date = parseTimestamp(value);
    if (!date) return '';
    const minutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    const days = Math.round(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
};

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const RowActions = ({ backup, downloading, disabled, onDownload, onRestore, onDelete }) => {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Button
                size="small"
                onClick={() => onDownload(backup.filename)}
                disabled={downloading}
                startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 600 }}
            >
                {downloading ? 'Downloading…' : 'Download'}
            </Button>
            <IconButton
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-label={`More actions for ${backup.filename}`}
                aria-haspopup="menu"
                disabled={disabled}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { close(); onRestore(backup); }}>
                    <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Restore from this backup" />
                </MenuItem>
                <MenuItem onClick={() => { close(); onDelete(backup); }} sx={{ color: DANGER }}>
                    <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: DANGER }} /></ListItemIcon>
                    <ListItemText primary="Delete backup" />
                </MenuItem>
            </Menu>
        </Box>
    );
};

const FileName = ({ filename }) => (
    <Typography
        variant="body2"
        title={filename}
        sx={{ color: INK, fontWeight: 500, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem', overflowWrap: 'anywhere' }}
    >
        {filename}
    </Typography>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const DatabaseManagement = () => {
    const { token } = useContext(AuthContext);
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [backupLoading, setBackupLoading] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [downloading, setDownloading] = useState(null);

    const [restoreTarget, setRestoreTarget] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') => setAlert({ open: true, message, severity });

    const fetchBackups = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await axios.get(`${API_URL}/admin/database/backups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBackups(response.data.data || []);
        } catch (error) {
            setLoadError(error.response?.data?.error || 'Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchBackups();
    }, [fetchBackups]);

    const handleBackup = async () => {
        try {
            setBackupLoading(true);
            await axios.post(
                `${API_URL}/admin/database/backup`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            notify('Backup created');
            fetchBackups();
        } catch (error) {
            notify(error.response?.data?.error || 'The backup could not be created.', 'error');
        } finally {
            setBackupLoading(false);
        }
    };

    const openRestore = (backup) => {
        setConfirmText('');
        setRestoreTarget(backup);
    };

    const closeRestore = () => {
        if (restoreLoading) return;
        setRestoreTarget(null);
        setConfirmText('');
    };

    const handleRestore = async () => {
        if (!restoreTarget || confirmText !== CONFIRM_WORD) return;

        try {
            setRestoreLoading(true);
            await axios.post(
                `${API_URL}/admin/database/restore/${restoreTarget.filename}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            notify('Database restored. Refresh the page to see the restored data.');
            setRestoreTarget(null);
            setConfirmText('');
        } catch (error) {
            notify(error.response?.data?.error || 'The database could not be restored.', 'error');
        } finally {
            setRestoreLoading(false);
        }
    };

    const handleDownload = async (filename) => {
        try {
            setDownloading(filename);
            const response = await fetch(`${API_URL}/admin/database/download/${filename}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            notify('The backup file could not be downloaded.', 'error');
        } finally {
            setDownloading(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        try {
            setDeleteLoading(true);
            await axios.delete(
                `${API_URL}/admin/database/backups/${deleteTarget.filename}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            notify('Backup deleted');
            setDeleteTarget(null);
            fetchBackups();
        } catch (error) {
            notify(error.response?.data?.error || 'The backup could not be deleted.', 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === 'clickaway') return;
        setAlert((prev) => ({ ...prev, open: false }));
    };

    const frameSx = { borderColor: RULE, borderRadius: 2, overflow: 'hidden' };
    const latest = backups[0];

    const renderList = () => {
        if (loading) {
            return (
                <Paper variant="outlined" sx={frameSx}>
                    <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true">
                        {[0, 1, 2].map((i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 3, py: 1.75, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
                                <Box sx={{ flex: 3 }}><Skeleton width="70%" /></Box>
                                <Box sx={{ flex: 2 }}><Skeleton width="60%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="50%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="80%" /></Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            );
        }

        if (loadError) {
            return (
                <Paper variant="outlined" sx={{ ...frameSx, p: { xs: 3, sm: 5 } }}>
                    <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Backups could not be loaded</Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>{loadError}</Typography>
                    <Button variant="outlined" onClick={fetchBackups} sx={{ textTransform: 'none' }}>Try again</Button>
                </Paper>
            );
        }

        if (!backups.length) {
            return (
                <Paper variant="outlined" sx={{ ...frameSx, p: { xs: 3, sm: 5 } }}>
                    <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No backups yet</Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>
                        Create a backup to save a copy of the database on the server. You can download it or restore from it later.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleBackup}
                        disabled={backupLoading}
                        startIcon={backupLoading ? <CircularProgress size={16} color="inherit" /> : <BackupOutlinedIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        {backupLoading ? 'Creating backup…' : 'Create backup'}
                    </Button>
                </Paper>
            );
        }

        return (
            <Paper variant="outlined" sx={frameSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: `1px solid ${RULE}` }}>
                    <Typography variant="body2" sx={{ color: MUTED }}>
                        {backups.length} {backups.length === 1 ? 'backup' : 'backups'}, newest first
                    </Typography>
                    <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={fetchBackups} sx={{ textTransform: 'none', color: MUTED }}>
                        Refresh
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 680 }}>
                        <TableHead>
                            <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25, whiteSpace: 'nowrap' } }}>
                                <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>File name</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Size</TableCell>
                                <TableCell align="right" sx={{ pr: 2 }}>
                                    <Box component="span" sx={visuallyHidden}>Actions</Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {backups.map((backup) => (
                                <TableRow
                                    key={backup.filename}
                                    sx={{
                                        '& td': { borderColor: RULE, py: 1.5, fontVariantNumeric: 'tabular-nums', color: INK },
                                        '&:last-of-type td': { borderBottom: 'none' }
                                    }}
                                >
                                    <TableCell sx={{ pl: { xs: 2, sm: 3 }, maxWidth: 360 }}>
                                        <FileName filename={backup.filename} />
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2" sx={{ color: INK }}>{formatDate(backup.timestamp)}</Typography>
                                        <Typography variant="caption" sx={{ color: MUTED }}>{formatRelative(backup.timestamp)}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatSize(backup.size)}</TableCell>
                                    <TableCell align="right" sx={{ pr: 2 }}>
                                        <RowActions
                                            backup={backup}
                                            downloading={downloading === backup.filename}
                                            disabled={restoreLoading}
                                            onDownload={handleDownload}
                                            onRestore={openRestore}
                                            onDelete={setDeleteTarget}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    return (
        <AppLayout>
            <Container maxWidth="lg" sx={{ pb: 6 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
                            Database backups
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
                            {!loading && !loadError && latest
                                ? `Last backup ${formatRelative(latest.timestamp).toLowerCase()}, on ${formatDate(latest.timestamp)}.`
                                : 'Back up the portal database, download copies and restore from a backup.'}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleBackup}
                        disabled={backupLoading || restoreLoading}
                        startIcon={backupLoading ? <CircularProgress size={16} color="inherit" /> : <BackupOutlinedIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {backupLoading ? 'Creating backup…' : 'Create backup'}
                    </Button>
                </Box>

                {backupLoading && (
                    <Paper variant="outlined" sx={{ ...frameSx, px: { xs: 2, sm: 3 }, py: 2, mb: 3 }} role="status" aria-live="polite">
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 500, mb: 1 }}>Creating backup</Typography>
                        <LinearProgress sx={{ height: 6, borderRadius: 3, bgcolor: RULE, '& .MuiLinearProgress-bar': { borderRadius: 3 } }} />
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>
                            This can take a few minutes for a large database. Keep this page open.
                        </Typography>
                    </Paper>
                )}

                {/* Restore warning */}
                <Box
                    role="note"
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        p: { xs: 2, sm: 2.5 },
                        mb: 3,
                        borderRadius: 2,
                        border: `1px solid ${WARNING_RULE}`,
                        bgcolor: WARNING_BG
                    }}
                >
                    <WarningAmberIcon sx={{ color: DANGER, mt: 0.25 }} />
                    <Box>
                        <Typography sx={{ fontWeight: 600, color: INK }}>Restoring overwrites the live database</Typography>
                        <Typography variant="body2" sx={{ color: INK, mt: 0.5, maxWidth: '75ch' }}>
                            A restore drops the current collections and replaces them with the backup. Anything created since that backup,
                            such as users, accounts, templates and billing records, is lost. Create a fresh backup before you restore.
                        </Typography>
                    </Box>
                </Box>

                {renderList()}

                {/* Restore confirmation */}
                <Dialog open={!!restoreTarget} onClose={closeRestore} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Replace the live database?</DialogTitle>
                    <DialogContent>
                        <DialogContentText component="div" sx={{ color: INK }}>
                            <Typography variant="body2" sx={{ color: INK, mb: 1 }}>
                                This replaces all current data with the contents of:
                            </Typography>
                            <Box sx={{ p: 1.5, mb: 2, borderRadius: 1, border: `1px solid ${RULE}`, bgcolor: '#F7F8FA' }}>
                                {restoreTarget && <FileName filename={restoreTarget.filename} />}
                                <Typography variant="caption" sx={{ color: MUTED }}>
                                    Created {formatDate(restoreTarget?.timestamp)}, {formatSize(restoreTarget?.size)}
                                </Typography>
                            </Box>
                        </DialogContentText>
                        <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
                            Every change made after this backup is permanently lost. Users may be signed out, and you can't undo this.
                        </Alert>
                        <TextField
                            label={`Type ${CONFIRM_WORD} to confirm`}
                            fullWidth
                            autoComplete="off"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            disabled={restoreLoading}
                            inputProps={{ spellCheck: false, 'aria-describedby': 'restore-confirm-help' }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && confirmText === CONFIRM_WORD) handleRestore();
                            }}
                        />
                        <Typography id="restore-confirm-help" variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.75 }}>
                            Enter the word in capital letters exactly as shown.
                        </Typography>
                        {restoreLoading && (
                            <Box sx={{ mt: 2.5 }} role="status" aria-live="polite">
                                <LinearProgress color="error" sx={{ height: 6, borderRadius: 3 }} />
                                <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 1 }}>
                                    Restoring the database. Keep this page open until it finishes.
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closeRestore} disabled={restoreLoading} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRestore}
                            color="error"
                            variant="contained"
                            disabled={restoreLoading || confirmText !== CONFIRM_WORD}
                            startIcon={restoreLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {restoreLoading ? 'Restoring…' : 'Replace database'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete confirmation */}
                <Dialog open={!!deleteTarget} onClose={deleteLoading ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Delete this backup?</DialogTitle>
                    <DialogContent>
                        {deleteTarget && <FileName filename={deleteTarget.filename} />}
                        <DialogContentText sx={{ color: MUTED, mt: 1.5 }}>
                            The file is removed from the server and can't be restored from later. The live database isn't affected.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                            disabled={deleteLoading}
                            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {deleteLoading ? 'Deleting…' : 'Delete backup'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={alert.open}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseAlert} severity={alert.severity} variant="filled" sx={{ width: '100%' }}>
                        {alert.message}
                    </Alert>
                </Snackbar>
            </Container>
        </AppLayout>
    );
};

const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1
};

export default DatabaseManagement;
